/**
 * Record now's hand-off to the recording bar (#14243).
 *
 * The bar is mounted in the ROOT LAYOUT, above the router, and it is the one
 * thing that may hold a microphone (`RecordingBarHost` owns the
 * `BarCaptureSession`). An upcoming row's **Record now** lives on a page. So
 * the page REQUESTS a capture into a note and the bar TAKES it — a one-slot
 * registry, the same page-publishes / bar-subscribes direction
 * `meeting-page-context.ts` uses, inverted: here the page speaks and the bar
 * acts.
 *
 * ## Every request is answered (#14265)
 *
 * A Record now that did nothing would be #13832 over again ("I can't find
 * any way to start recording"), so {@link requestNoteRecording} resolves to
 * what HAPPENED, and the caller says so whenever it is not `started`:
 *
 *   - `started` — the microphone is open and recording into the note;
 *   - `busy` — a capture is already running (the bar holds one microphone);
 *   - `starting` — another start is already in flight;
 *   - `refused` — the start was refused (the server, the browser, the
 *     microphone permission) — the bar shows its own reason;
 *   - `unavailable` — no bar took it. The bar registers only while ENABLED
 *     and not suppressed, and it cannot know it is enabled until its flag
 *     read resolves, so a press made while that read is still loading WAITS
 *     up to {@link NOTE_RECORDING_HANDLER_WAIT_MS} for a registration rather
 *     than answering `unavailable` about a bar that is about to mount.
 *   - `cancelled` — the person dismissed the bar's consent prompt (#14422).
 *
 * ## Consent comes first (#14422)
 *
 * The bar asks before any microphone opens. For Record now it has to ask
 * before the NOTE exists too — Cancel must leave nothing behind — so the page
 * asks the bar for the acknowledgement alone ({@link requestRecordingConsent})
 * BEFORE it finds or creates the note, then sends its recording request with
 * `acknowledged: true` so the bar does not ask a second time. A request
 * without it gets the prompt, like every other entry point.
 *
 * ## The notes header (#15623)
 *
 * `/notes`' "Record a note", "Upload a recording" and "Test your microphone"
 * are the bar's own Record, upload dialog and microphone check. The header
 * asks for them with {@link requestRecordingBarAction}, under the same slot
 * rules — and the same "every request is answered" — as Record now.
 *
 * Client-safe: no `server-only`, no Prisma, no DOM.
 */

export type NoteRecordingRequest = {
  /** The note to record into — already found or created server-side. */
  noteId: string
  /** What the bar names the capture while it runs; `null` for no name. */
  title: string | null
  /**
   * The person already accepted the bar's consent prompt for THIS press,
   * through {@link requestRecordingConsent} (#14422). Absent or `false`, the
   * bar shows the prompt before it starts anything.
   */
  acknowledged?: boolean
  /**
   * The note's calendar event or meeting has a video call this viewer can
   * join (#15666): the start then defaults to the microphone plus computer
   * audio. Absent reads as `false`.
   */
  hasVirtualMeeting?: boolean
}

/** What a request came to — see the header. */
export type NoteRecordingOutcome =
  | 'started'
  | 'busy'
  | 'starting'
  | 'refused'
  | 'unavailable'
  | 'cancelled'

/** What a consent ask came to — see "Consent comes first" above. */
export type RecordingConsentOutcome =
  | 'accepted'
  | 'cancelled'
  | 'busy'
  | 'starting'
  | 'unavailable'

type Handler = (request: NoteRecordingRequest) => Promise<NoteRecordingOutcome>
/**
 * What the page knows about the capture it is asking consent for (#15666):
 * whether its event has a video call, so the prompt can say the capture
 * includes computer audio — the start's default for one — before it starts.
 */
export type RecordingConsentRequest = { hasVirtualMeeting?: boolean }

type ConsentHandler = (
  request: RecordingConsentRequest,
) => Promise<RecordingConsentOutcome>

/** How long a request waits for a bar that has not registered yet. */
export const NOTE_RECORDING_HANDLER_WAIT_MS = 3000

/**
 * One registration slot: the newest registration wins, a stale unregister
 * cannot remove a newer one (a remount registers before the old instance's
 * cleanup runs), and an ask made before anything registered waits.
 */
function createSlot<H>() {
  let current: H | null = null
  const waiters = new Set<() => void>()
  return {
    register(handler: H): () => void {
      current = handler
      for (const wake of [...waiters]) wake()
      return () => {
        if (current === handler) current = null
      }
    },
    /** Resolves once a handler is registered, or after `waitMs` without one. */
    wait(waitMs: number): Promise<H | null> {
      if (current !== null) return Promise.resolve(current)
      return new Promise(resolve => {
        const done = () => {
          clearTimeout(timer)
          waiters.delete(done)
          resolve(current)
        }
        const timer = setTimeout(done, waitMs)
        waiters.add(done)
      })
    },
  }
}

const recordingSlot = createSlot<Handler>()
const consentSlot = createSlot<ConsentHandler>()

/** The bar's side: take recording requests until the returned function runs. */
export function registerNoteRecordingHandler(handler: Handler): () => void {
  return recordingSlot.register(handler)
}

/** The bar's side of the consent ask (#14422), with the same slot rules. */
export function registerRecordingConsentHandler(
  handler: ConsentHandler,
): () => void {
  return consentSlot.register(handler)
}

/** The page's side. Anything but `started` is something to tell the person. */
export async function requestNoteRecording(
  request: NoteRecordingRequest,
  waitMs: number = NOTE_RECORDING_HANDLER_WAIT_MS,
): Promise<NoteRecordingOutcome> {
  const handler = await recordingSlot.wait(waitMs)
  if (handler === null) return 'unavailable'
  try {
    return await handler(request)
  } catch {
    return 'refused'
  }
}

/**
 * Ask the bar to show its consent prompt, and nothing else (#14422). Only
 * `accepted` may go on to create a note; a bar that throws answers
 * `cancelled`, never consent.
 */
export async function requestRecordingConsent(
  request: RecordingConsentRequest = {},
  waitMs: number = NOTE_RECORDING_HANDLER_WAIT_MS,
): Promise<RecordingConsentOutcome> {
  const handler = await consentSlot.wait(waitMs)
  if (handler === null) return 'unavailable'
  try {
    return await handler(request)
  } catch {
    return 'cancelled'
  }
}

// ---------------------------------------------------------------------------
// The notes header's bar actions (#15623)
// ---------------------------------------------------------------------------

/**
 * What the notes header asks the bar to do: its own **Record** (the bar's
 * `handleRecord` — consent, then a capture into the meeting under way or a
 * new note), its **Upload a recording** dialog, or its **microphone check**.
 * The header is a second way IN to the bar's flows, never a new path.
 *
 * Since #15666 the bar is hidden while nothing is captured, so these are the
 * ONLY ways in: the meeting page's Record and the note page's Add a
 * recording ask for `record` (the bar's surface decides what it records
 * into), and Home's quick record asks for `record-new-note` — the menu's
 * "Record a new note", a capture into a NEW note that never attaches to the
 * next meeting, as `record` would to one about to start.
 */
export type RecordingBarAction =
  | 'record'
  | 'record-new-note'
  | 'upload'
  | 'mic-check'

/**
 * What a bar action came to: a record's outcome, `opened` for the upload
 * dialog or the microphone check, or `unavailable` when no bar took it.
 */
export type RecordingBarActionOutcome = NoteRecordingOutcome | 'opened'

type BarActionHandler = (
  action: RecordingBarAction,
) => Promise<RecordingBarActionOutcome>

const barActionSlot = createSlot<BarActionHandler>()

/** The bar's side: take header actions until the returned function runs. */
export function registerRecordingBarActionHandler(
  handler: BarActionHandler,
): () => void {
  return barActionSlot.register(handler)
}

/**
 * The page's side (#15623). Waits for a bar that has not registered yet, as
 * Record now does; a bar that throws answers `refused`.
 */
export async function requestRecordingBarAction(
  action: RecordingBarAction,
  waitMs: number = NOTE_RECORDING_HANDLER_WAIT_MS,
): Promise<RecordingBarActionOutcome> {
  const handler = await barActionSlot.wait(waitMs)
  if (handler === null) return 'unavailable'
  try {
    return await handler(action)
  } catch {
    return 'refused'
  }
}
